import clsx from 'clsx';
import { getYear } from 'date-fns';
import { useState } from 'react';
import { Direction } from 'types/Utilities';
import { Slider } from 'components/Pages/Home';
import Icon from 'components/ui/Icon';
import tailwind from 'utils/breakpoints';
import { isFiniteNumber } from 'utils/number';
import { getStaffFullName } from 'utils/staff';
import { useTranslation } from 'hooks/useTranslation';
import type { ClinicDoctor } from 'types/Clinic';

type Props = {
  doctors: ClinicDoctor[];
};

const Team = ({ doctors }: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const items =
    doctors
      ?.map((item) => {
        const qunoscore = item?.qunoscoreDetails?.qunoscore?.['en-US'];
        const treatmentsPerformedLastYear =
          item?.qunoscoreDetails?.treatmentsPerformedLastYear?.['en-US'];
        const yearsOfExperienceAsDoctor =
          item?.qunoscoreDetails?.yearsOfExperienceAsDoctor?.['en-US'];

        return {
          disableLocale: item.disableLocale,
          slug: item.slug,
          name: getStaffFullName(item),
          image: item.doctorImage?.url,
          qunoscore: isFiniteNumber(Number(qunoscore))
            ? Number(qunoscore)
            : undefined,
          treatmentsPerformedLastYear: isFiniteNumber(
            Number(treatmentsPerformedLastYear),
          )
            ? Number(treatmentsPerformedLastYear)
            : undefined,
          yearsOfExperience:
            yearsOfExperienceAsDoctor &&
            isFiniteNumber(Number(yearsOfExperienceAsDoctor))
              ? getYear(new Date()) - Number(yearsOfExperienceAsDoctor)
              : undefined,
        };
      })
      .filter((item) => !item.disableLocale) || [];

  if (!items.length) {
    return null;
  }

  return (
    <div id="team" className="border-t border-silver-light md:pt-18">
      <div className="max-w-screen-xl mx-auto px-5 md:px-4">
        <div className="md:grid grid-cols-3">
          <div className="flex justify-between items-center md:items-start">
            <h2 className="adelle-bold-section-headline">
              {t('clinicPage.medicalTeam')}
            </h2>
            <Icon
              type="Arrow"
              direction={isCollapsed ? Direction.BOTTOM : Direction.TOP}
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="md:hidden h-4 w-4 mb-6 text-navy stroke-current mt-12"
              color={tailwind.theme.backgroundColor.navy}
            />
          </div>
          <div className={clsx(isCollapsed && 'hidden', 'md:block col-span-2')}>
            <h3
              className="md:ml-3 lg:ml-0 pt-0 font-adelle font-bold"
              style={{ fontSize: '1.125rem !important' }}
            >
              {t('doctorsList.doctors')}
            </h3>
            <Slider
              type="popularDoctors"
              items={items}
              sliderItemClasses={clsx(items.length > 1 && 'mx-1 md:mx-2')}
              nonSliderItemClasses={clsx(
                'max-w-xs',
                items.length > 1 && 'md:mx-3',
              )}
              smallScreenViewPorts="767px"
              sliderConfig={{
                threshold: 2,
                responsive: {
                  desktop: {
                    breakpoint: { max: 4000, min: 1024 },
                    items: 2,
                    partialVisibilityGutter: 60,
                  },
                  tablet: {
                    breakpoint: { max: 1023, min: 640 },
                    items: 1,
                    partialVisibilityGutter: 60,
                  },
                  mobile: {
                    breakpoint: { max: 639, min: 0 },
                    items: 1,
                    partialVisibilityGutter: 60,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Team;
